// pages/ExportScreen.jsx

import { useState, useEffect, useCallback } from "react";
import "../../../styles/pages/exportscreen.css";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";

// ── Constants ──────────────────────────────────────────────────────────────
const FORMAT_OPTIONS = [
  { value: "pdf",  label: "PDF (.pdf)"  },
  { value: "xlsx", label: "Excel (.xlsx)" },
  { value: "csv",  label: "CSV (.csv)"  },
  { value: "json", label: "JSON (.json)" },
];

// ── API helpers ────────────────────────────────────────────────────────────
const fetchExportData = async (reportingYear) => {
  const res = await apiClient.get("/api/brsr/disclosure/export", {
    params: { reporting_year: reportingYear },
  });
  return res.data.data;
};

const saveExportHistory = async (payload) => {
  const res = await apiClient.post("/api/brsr/disclosure/export-history", payload);
  return res.data;
};

// ── Sub-components ─────────────────────────────────────────────────────────
const SectionList = ({ items }) => (
  <ul className="ex-section-list">
    {items.map((s) => (
      <li key={s.submission_id} className="ex-section-list__item">
        {s.section_name}
      </li>
    ))}
  </ul>
);

const HistoryTable = ({ history }) => (
  <table className="ex-history-table">
    <thead>
      <tr>
        <th className="ex-history-table__th">File Name</th>
        <th className="ex-history-table__th">Format</th>
        <th className="ex-history-table__th">Sections</th>
        <th className="ex-history-table__th">Generated At</th>
        <th className="ex-history-table__th">Download</th>
      </tr>
    </thead>
    <tbody>
      {history.map((row) => (
        <tr key={row.id}>
          <td className="ex-history-table__td">{row.file_name}</td>
          <td className="ex-history-table__td">
            <span className="ex-format-badge">{row.format}</span>
          </td>
          <td className="ex-history-table__td">{row.sections_count}</td>
          <td className="ex-history-table__td">{row.generated_on}</td>
          <td className="ex-history-table__td">
            <a href={row.file_path} className="ex-download-link">⬇ Download</a>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

// ── Main Component ─────────────────────────────────────────────────────────
const ExportScreen = () => {
  const { reportingYear } = useReportingYear();

  const [format,        setFormat]        = useState("pdf");
  const [lockedCount,   setLockedCount]   = useState(0);
  const [lockedItems,   setLockedItems]   = useState([]);   // { principle_id, section_name, submission_id }[]
  const [notLockedCount,setNotLockedCount]= useState(0);
  const [notLockedItems,setNotLockedItems]= useState([]);   // { principle_id, section_name, submission_id }[]
  const [history,       setHistory]       = useState([]);
  const [fetchLoading,  setFetchLoading]  = useState(false);
  const [genLoading,    setGenLoading]    = useState(false);
  const [error,         setError]         = useState(null);

  // ── GET ───────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!reportingYear) return;

    setFetchLoading(true);
    setError(null);
    setLockedCount(0);
    setLockedItems([]);
    setNotLockedCount(0);
    setNotLockedItems([]);
    setHistory([]);

    try {
      const data = await fetchExportData(reportingYear);
      setLockedCount(data.locked_count);
      setLockedItems(data.locked ?? []);
      setNotLockedCount(data.not_locked_count);
      setNotLockedItems(data.not_locked ?? []);
      setHistory(data.export_history ?? []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setFetchLoading(false);
    }
  }, [reportingYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── POST ──────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    // NOTE: allow export even when lockedCount is 0 so the button always works.
    // The API will validate on its end. Remove this guard or adjust as needed.
    setGenLoading(true);
    setError(null);

    const ext      = format.toLowerCase();
    const fileName = `brsr_disclosure_fy_${reportingYear.replace("-", "_")}.${ext}`;

    const payload = {
      reporting_year: reportingYear,
      report_name:    `BRSR Disclosure — FY ${reportingYear}`,
      export_format:  format.toUpperCase(),
      file_name:      fileName,
      file_path:      `/uploads/brsr/${fileName}`,
      file_size:      0,
      // sections:       lockedItems.map((s) => ({
      //   principle_id: s.principle_id,
      //   section_name: s.section_name,
      // })),

      sections: [
      { principle_id: 1, section_name: "Ethics, transparency & accountability" },
      { principle_id: 2, section_name: "Sustainable goods & services" },
      ],
    };

    console.log("POST payload →", payload); // ← confirm it's firing

    try {
      await saveExportHistory(payload);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setGenLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="ex-page">

      {/* Page Header */}
      <div className="ex-page-title">Export Screen</div>
      <div className="ex-page-subtitle">
        Export approved &amp; locked BRSR disclosure data for SEBI filing
      </div>
      <div className="ex-reporting-bar">
        <strong>Reporting Year: FY {reportingYear}</strong>
        &nbsp;·&nbsp; Only approved and locked disclosures are included in exports.
      </div>

      {/* Error banner */}
      {error && (
        <div className="ex-error-banner">⚠ {error}</div>
      )}

      {/* Summary Cards */}
      <div className="ex-summary-row">

        {/* Locked card */}
        <div className="ex-summary-card">
          <span className="ex-summary-card__label">Sections Ready for Export</span>
          <span className="ex-summary-card__value">
            <span className="ex-summary-card__icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </span>
            {fetchLoading ? "…" : lockedCount}
          </span>
          {/* Section names listed below the count */}
          {!fetchLoading && lockedItems.length > 0 && (
            <SectionList items={lockedItems} />
          )}
          {!fetchLoading && lockedItems.length === 0 && (
            <p className="ex-section-empty">No locked sections</p>
          )}
        </div>

        {/* Not locked card */}
        <div className="ex-summary-card">
          <span className="ex-summary-card__label">Not Yet Locked</span>
          <span className="ex-summary-card__value">
            {fetchLoading ? "…" : notLockedCount}
          </span>
          {/* Section names listed below the count */}
          {!fetchLoading && notLockedItems.length > 0 && (
            <SectionList items={notLockedItems} />
          )}
          {!fetchLoading && notLockedItems.length === 0 && (
            <p className="ex-section-empty">All sections locked</p>
          )}
        </div>

      </div>

      {/* Generate New Export */}
      <div className="ex-panel">
        <div className="ex-panel__header">
          <span className="ex-panel__title">Generate New Export</span>
        </div>
        <div className="ex-generate-body">

          <div className="ex-field">
            <label className="ex-field__label">Format</label>
            <select
              className="ex-select"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              disabled={genLoading}
            >
              {FORMAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="ex-field">
            <label className="ex-field__label">Scope</label>
            <div className="ex-scope-display">
              {lockedCount} locked section{lockedCount !== 1 ? "s" : ""} — approved data
            </div>
          </div>

          {/* ✅ Removed lockedCount === 0 disable so button always clickable */}
          <button
            className="ex-btn-generate"
            onClick={handleGenerate}
            disabled={genLoading || fetchLoading}
          >
            {genLoading ? "Generating…" : "Generate Export"}
          </button>

        </div>
      </div>

      {/* Export History */}
      <div className="ex-panel">
        <div className="ex-panel__header">
          <span className="ex-panel__title">Export History</span>
        </div>
        <div className="ex-history-body">
          {fetchLoading ? (
            <div className="ex-empty-state">Loading…</div>
          ) : history.length === 0 ? (
            <div className="ex-empty-state">
              <span className="ex-empty-state__icon">📄</span>
              <span className="ex-empty-state__text">
                No exports generated yet. Lock approved sections and generate your first export above.
              </span>
            </div>
          ) : (
            <HistoryTable history={history} />
          )}
        </div>

        <div className="ex-footer">
          <a href="/dashboard/disclosure-builder" className="ex-footer-link">← Back to Disclosure Builder</a>
          <a href="/dashboard/audit-evidence" className="ex-footer-link">Go to Audit &amp; Evidence →</a>
        </div>
      </div>

    </div>
  );
};

export default ExportScreen;
